import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber, Table } from 'antd';
import { TFunction } from 'i18next';
import { useEffect, useState } from 'react';
import './averageTareTable.scss';

export interface TareDataType {
  key: string;
  id: number;
  tareValue: number | null;
}

interface AverageTareTableProps {
  t: TFunction;
  handleCancel?: () => void;
  handleBack?: () => void;
  handleNext?: () => void;
  tareData: TareDataType[];
  setTareData?: React.Dispatch<React.SetStateAction<TareDataType[]>>;
  isViewMode?: boolean;
  tareError?: string | null;
  setTareError?: React.Dispatch<React.SetStateAction<string | null>>;
}
/* const initialTare = [
  {
    key: '1',
    id: 1,
    tareValue: 0,
  },
]; */

const AverageTareTable: React.FC<AverageTareTableProps> = ({
  t,
  tareData,
  setTareData,
  isViewMode = false,
  tareError,
  setTareError,
}) => {
  // const [tareData, setTareData] = useState<TareDataType[]>(initialTare);

  useEffect(() => {
    if (tareError) {
      const hasEmptyTare = tareData.some((tare) => !tare.tareValue);
      if (hasEmptyTare) {
        if (tareError !== t('fillAllTareValuesError')) {
          setTareError?.(t('fillAllTareValuesError'));
        }
      } else{
        setTareError?.(null);
      }
    }
  }, [tareData, tareError, t, setTareError]);
  
  /* Tare value change */
  const handleTareChange = (key: string, value: number | null) => {
    const newValue = value === null || value === undefined ? 0 : value;
    setTareData?.(
      tareData.map((item) => (item.key === key ? { ...item, tareValue: newValue } : item))
    );
  };

  /* Tare row delete */
  const deleteTare = (key: string) => {
    if (tareData?.length > 1) {
      const newTare = tareData?.filter((tare) => tare?.key !== key);

      const reIndexedTare = newTare?.map((tare, index) => ({
        ...tare,
        id: index + 1,
      }));

      setTareData?.(reIndexedTare);
    }
  };

  /* Add new row for Tare */
  const addTare = () => {
    const hasEmptyTare = tareData.some((tare) => !tare.tareValue);

    if (hasEmptyTare) {
      setTareError?.(t('fillAllTareValuesError'));
      return;
    }

    setTareError?.(null);
    const newKey = Date.now().toString();

    const newRow: TareDataType = {
      key: newKey,
      id: tareData?.length + 1,
      tareValue: 0,
    };

    setTareData?.([...tareData, newRow]);
  };

  const columns = [
    {
      title: <div className="text-center">{t('averageTareTableHeading')}</div>,
      children: [
        {
          title: t('no.'),
          dataIndex: 'id',
          key: 'id',
          width: '30%',
        },
        {
          title: t('tare'),
          dataIndex: 'tareValue',
          key: 'tareValue',
          render: (value: number, record: TareDataType) => (
            <InputNumber
              type="number"
              className="view-mode-form w-full"
              disabled={isViewMode}
              value={value}
              step="0.01"
              min={0}
              status={value === null || value < 0 ? 'error' : ''}
              placeholder={t('tarePlaceholder')}
              onWheel={(e) => e.currentTarget.blur()}
              controls={true}
              onChange={(val) => handleTareChange(record?.key, val)}
              onKeyDown={(e) => {
                if (e.key === 'e' || e.key === 'E' || e.key === '-' || e.key === '+') {
                  e.preventDefault();
                }
              }}
              formatter={(value) => {
                if (!value) return '';
                return String(value).replace(/^(\d*\.?\d{0,2}).*/, '$1');
              }}
              /* onKeyDown={(e) => {
                const isNumber = /[0-9]/.test(e.key);
                const isFirstDecimal = e.key === '.' && !value?.toString().includes('.');
                const isControlKey = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'];

                if (!isNumber && !isFirstDecimal && !isControlKey) {
                  e.preventDefault();
                }
              }} */
            />
          ),
        },
      ],
    },
    {
      title: '',
      // title: t('action'),
      key: 'action',
      // width: 100,
      width: 0,
      onHeaderCell: () =>
        ({
          classNames: 'action-column-cell',
        }) as React.HtmlHTMLAttributes<HTMLElement>,
      /* onCell: () => ({
        classNames: 'border-none p-0 w-0 relative'
      }), */
      classNames: 'border-none action-column-cell',
      render: (_: any, record: TareDataType, index: number) => (
        <div className="absolute top-1/2 flex -translate-y-1/2 items-center">
          {index !== 0 && !isViewMode && (
            <Button
              type="text"
              danger
              disabled={index === 0}
              // hidden= {index === 0}
              icon={<DeleteOutlined />}
              onClick={() => deleteTare(record?.key)}
              className=""
              /* style={{
                marginLeft: '12px',
                zIndex:10
              }} */
            />
          )}
        </div>
      ),
    },
  ];

  /* Calculate Average */
  const calculateAverageTare = (data: readonly TareDataType[]) => {
    const validTares = data.filter((tare) => tare.tareValue !== null && tare.tareValue > 0);

    const totalTare = validTares.reduce((sum, tare) => sum + (tare.tareValue || 0), 0);

    const averageTare = validTares?.length > 0 ? totalTare / validTares?.length : 0;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row className="bg-gray-50 font-bold">
          <Table.Summary.Cell index={0}>{t('averageTare')}</Table.Summary.Cell>
          <Table.Summary.Cell index={1}>
            <span className="text-gray-700">{averageTare?.toFixed(2)}</span>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} />
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div className={`m-0 w-full ${isViewMode ? 'p-2' : 'p-8'}`}>
      <div className="mb-4 space-y-2">
        <h2 className="border-b border-gray-200 pb-3 text-xl font-semibold text-gray-900">
          {t('weightCalculationHeading')}
        </h2>
      </div>
      <div className={`relative ${isViewMode ? 'ml-2' : 'ml-8'}`}>
        {!isViewMode && (
          <div className="absolute -left-12 top-[98px] z-10">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addTare}
              disabled={tareData.some((tare) => !tare.tareValue)}
            ></Button>
          </div>
        )}
        {/* </div> */}
        {/* <div>
        <Button type="primary" icon={<PlusOutlined />} onClick={addTare}></Button>
      </div> */}
        <div className="max-w-2xl">
          <Table
            dataSource={tareData}
            columns={columns}
            bordered
            pagination={false}
            summary={calculateAverageTare}
            // rowClassName="h-[54px]"
            className="average-Tare-Table"
            // scroll={{ x: undefined, y: tareData?.length > 10 ? 400 : undefined }}
          />
          {!isViewMode && tareData?.length > 1 && (
            <div className="mt-2 text-sm text-gray-500">
              *{t('tareZeroValueNote')}
            </div>
          )}
          {tareError && (
            <div className="mt-2 text-md text-red-500">
              {tareError}
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default AverageTareTable;
